import React, { useState, useEffect, useCallback } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { Button, Icon, IconButton, Snackbar } from '@material-ui/core';
import Http from '../../../../Http';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CfdiContacto = ({ match, id_cliente: propClienteId }) => {
  const routeParams = useParams() || {};
  const clientId = propClienteId || match?.params?.id || routeParams?.id;

  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const loadContactos = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await Http.get(`cliente/contactos-cfdi?c=${clientId}`);
      if (res && !res.error && Array.isArray(res.data)) {
        setContactos(res.data);
      } else {
        setContactos([]);
      }
    } catch (e) {
      console.error('Error al cargar contactos CFDI', e);
      setContactos([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadContactos();
  }, [loadContactos]);

  const handleAdd = () => {
    if (contactos.length >= 6) {
      setToast({ open: true, message: 'Máximo 6 contactos de facturación permitidos', severity: 'warning' });
      return;
    }
    setContactos((prev) => [
      ...prev,
      {
        id: null,
        id_cliente: clientId,
        nombre: '',
        email: '',
      },
    ]);
  };

  const handleChange = (index, field, value) => {
    setContactos((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleDelete = async (index) => {
    const target = contactos[index];
    if (target.id) {
      try {
        const res = await Http.post('cliente/contacto-cfdi/delete', { id: target.id });
        if (res && !res.error) {
          setToast({ open: true, message: 'Contacto eliminado', severity: 'success' });
          loadContactos();
          return;
        }
      } catch (e) {
        console.error('Error al eliminar contacto CFDI', e);
      }
    }
    setContactos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (contactos.length === 0) return;

    for (let i = 0; i < contactos.length; i++) {
      const c = contactos[i];
      if (!c.nombre || c.nombre.trim().length < 2) {
        setToast({
          open: true,
          message: `El nombre es requerido en el contacto #${i + 1}`,
          severity: 'error',
        });
        return;
      }
      if (!c.email || !EMAIL_REGEX.test(c.email.trim())) {
        setToast({
          open: true,
          message: `Correo electrónico inválido en el contacto #${i + 1}`,
          severity: 'error',
        });
        return;
      }
    }

    setSaving(true);
    try {
      const res = await Http.post('cliente/contactos-cfdi/save', {
        contactos: contactos.map((c) => ({ ...c, id_cliente: clientId })),
      });
      if (res && !res.error) {
        setToast({ open: true, message: 'Contactos CFDI guardados correctamente', severity: 'success' });
        loadContactos();
      } else {
        setToast({ open: true, message: res?.errMsg || 'Error al guardar contactos CFDI', severity: 'error' });
      }
    } catch (e) {
      console.error('Error al guardar contactos CFDI', e);
      setToast({ open: true, message: 'Error de conexión al guardar contactos', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-xs border border-gray-100 p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-2 mb-3 border-b border-gray-100 gap-2">
        <div>
          <h3 className="text-sm font-bold text-gray-800 tracking-tight">Contactos para Envío de CFDI</h3>
          <p className="text-xs text-gray-500">
            Correos a los que se enviará automáticamente la factura electrónica (XML y PDF)
          </p>
        </div>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<Icon>add</Icon>}
          onClick={handleAdd}
          className="!capitalize !text-xs"
        >
          Agregar Contacto
        </Button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400">Cargando contactos...</div>
      ) : contactos.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          No hay contactos de facturación registrados. Haga clic en &quot;Agregar Contacto&quot; para registrar uno.
        </div>
      ) : (
        <div className="space-y-2.5">
          {contactos.map((c, i) => (
            <div
              key={c.id || i}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 p-2.5 bg-gray-50/75 rounded-lg border border-gray-100"
            >
              <div className="flex-1">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                  Nombre del Contacto
                </label>
                <input
                  type="text"
                  value={c.nombre || ''}
                  onChange={(e) => handleChange(i, 'nombre', e.target.value)}
                  placeholder="Ej. C.P. María López"
                  className="w-full px-2.5 py-1 text-xs text-gray-900 bg-white border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="flex-1">
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                  Correo Electrónico (Facturación)
                </label>
                <input
                  type="email"
                  value={c.email || ''}
                  onChange={(e) => handleChange(i, 'email', e.target.value)}
                  placeholder="facturacion@empresa.com"
                  className="w-full px-2.5 py-1 text-xs text-gray-900 bg-white border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="self-end sm:self-center pt-2 sm:pt-4">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(i)}
                  className="!text-red-500 hover:!bg-red-50"
                  aria-label="Eliminar contacto"
                >
                  <Icon fontSize="small">delete_outline</Icon>
                </IconButton>
              </div>
            </div>
          ))}

          <div className="pt-2 flex justify-end">
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSave}
              disabled={saving}
              className="!capitalize !text-xs !px-4"
            >
              {saving ? 'Guardando...' : 'Guardar Contactos CFDI'}
            </Button>
          </div>
        </div>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
      />
    </div>
  );
};

export default withRouter(CfdiContacto);
